import 'react-datepicker/dist/react-datepicker.css';

import * as locale from 'date-fns/locale';
import React, {FC, useEffect, useState} from 'react';
import DatePicker, {registerLocale} from 'react-datepicker';
import {useSelector} from 'react-redux';

import AppButton from '../../../components/Button/AppButton';
import ConnectionTrackButton from '../../../components/Button/ConnectionTrackButton/ConnectionTrackButton';
import ArticlePageLayout from '../../../components/DataDisplay/ArticlePageLayout';
import PopupFullPage from '../../../components/Popup/PopupFullPage';
import appConfig from '../../../config/appConfig';
import * as localizationKeys from '../../../consts/localization/localizationKeys';
import analyticsService from '../../../services/analyticsService';
import {getLocalizedStrings, getMappedInitialLanguageCode} from '../../../services/localization/localizationService';
import modalService from '../../../services/modalService';
import {selectIotDeviceType} from '../../../state/selectors/iotDevice';
import elementsTesIds from '../../../test/consts/elementsTesIds';
import CustomInput from './components/CustomInput/CustomInput';
import styles from './PurchaseDateFullPagePopup.module.scss';
import {IPurchaseDateFullPagePopup} from './PurchaseDateFullPagePopupTypes';

const CUSTOM_LOCALE_NAME = 'customLocale';

const PurchaseDateFullPagePopup: FC<IPurchaseDateFullPagePopup> = ({modalType, registrationAction}) => {
    const [selectedDate, setSelectedDate] = useState<undefined | null | Date>(undefined);
    const isEmptyDate = selectedDate === null;
    const minDateYearLimitation = appConfig.getMinDateYearLimitation() || 5;
    const calendarStartDay = appConfig.getCalendarStartDay() || 1;
    const purchaseDateLocalizations = appConfig.getPurchaseDateLocalizations() || [];
    const currentLocalizedLanguage = getMappedInitialLanguageCode().toUpperCase();
    const purchaseLocalization = purchaseDateLocalizations.find(
        (langItem) => langItem.language === currentLocalizedLanguage
    );
    const defaultInputMask = [/[1-9]/, /\d/, /\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/];
    const {datePickerLanguage = 'enUS', inputMask = defaultInputMask, dateFormat} = purchaseLocalization || {};
    const now = new Date();
    const minCalendarData = new Date(now.setFullYear(now.getFullYear() - minDateYearLimitation));
    const maxCalendarData = new Date();
    const localizedStrings = getLocalizedStrings();
    const deviceType = useSelector(selectIotDeviceType);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    registerLocale(CUSTOM_LOCALE_NAME, locale[datePickerLanguage]);

    const onChange = (value: any) => {
        setSelectedDate(value);
    };

    const onContinueClick = () => {
        if (selectedDate) {
            const preparedPurchaseDate = selectedDate?.toISOString();

            registrationAction(preparedPurchaseDate);
            modalService.hideModal(modalType);
        } else {
            setSelectedDate(null);
        }
    };

    useEffect(() => {
        analyticsService.pushRegistrationPurchaseDateEvent(deviceType);
    }, []);

    return (
        <PopupFullPage contentLabel={localizedStrings[localizationKeys.PURCHASE_DATE_ENTRY_REGISTRATION_TITLE]}>
            <ArticlePageLayout
                titleText={localizedStrings[localizationKeys.PURCHASE_DATE_ENTRY_REGISTRATION_TITLE]}
                titleClassName='mb-4 xl:mb-8'
                descriptionText={localizedStrings[localizationKeys.PURCHASE_DATE_ENTRY_DESCRIPTION]}
                descriptionClassName='mb-9'
                containerClassName='pt-12 pb-[72px] lg:pt-[60px] xl:pb-[167px]'
            >
                <div className='w-full'>
                    <DatePicker
                        locale={CUSTOM_LOCALE_NAME}
                        dateFormat={dateFormat}
                        onChange={onChange}
                        calendarClassName={styles.Calendar}
                        placeholderText={localizedStrings[localizationKeys.PURCHASE_DATE_ENTRY_TEXTBOX_PLACEHOLDER]}
                        calendarStartDay={calendarStartDay}
                        selected={selectedDate}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode='select'
                        fixedHeight
                        minDate={minCalendarData}
                        maxDate={maxCalendarData}
                        popperPlacement='bottom'
                        customInput={
                            <CustomInput
                                isEmptyDate={isEmptyDate}
                                inputMask={inputMask}
                                inputLabelText={localizedStrings[localizationKeys.PURCHASE_DATE_ENTRY_TEXTBOX_TITLE]}
                                incorrectDateErrorText={
                                    localizedStrings[localizationKeys.PURCHASE_DATE_ENTRY_INVALID_DATE_ERROR_TEXT]
                                }
                                testId={elementsTesIds.DEVICE_REGISTRATION_INPUT_DATE}
                            />
                        }
                    />
                    <ConnectionTrackButton
                        primary
                        light
                        button={AppButton}
                        onClick={onContinueClick}
                        className='mx-auto mt-[105px] lg:mt-[80px]'
                        testId={elementsTesIds.DEVICE_REGISTRATION_BTN_CONTINUE}
                    >
                        {localizedStrings[localizationKeys.PURCHASE_DATE_ENTRY_CONTINUE_BUTTON]}
                    </ConnectionTrackButton>
                </div>
            </ArticlePageLayout>
        </PopupFullPage>
    );
};

export default PurchaseDateFullPagePopup;
